import { useCallback, useEffect, useState } from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { useRxDatabase } from 'rxdb/plugins/react';
import type {
    DrawingSheetDocType,
    Subject,
    FindDocType,
    FindingDocType,
    FindingPlanumDocType,
    FindingProfileDocType,
    MediaDocType,
    MediaLinkDocType,
    OverallDescriptionDocType,
    PhotoDocType,
    SampleDocType
} from '@ast-data/shared';
import type { MangoQuery, RxCollection } from 'rxdb';
import type { AppDatabase } from '../../db/database';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';
import { applyMediaRemoval } from '../media/ownerMediaRemoval';
import { planumRefLabel, profileRefLabel } from './findingRefLabels';
import {
    planDeletion,
    relationOfField,
    type DeletionPlan,
    type DeletionTarget,
    type RecordType,
    type ReferenceRemoval,
    type ReferencingType
} from './deletionPlan';
import { compareNatural } from '../../utils/compare';
import { removeOwnerTranslations } from '../translation/ownerTranslations';

interface FamilyRecords {
    findings: FindingDocType[];
    planums: FindingPlanumDocType[];
    profiles: FindingProfileDocType[];
    finds: FindDocType[];
    samples: SampleDocType[];
    photos: PhotoDocType[];
    drawingSheets: DrawingSheetDocType[];
    overallDescriptions: OverallDescriptionDocType[];
    media: MediaDocType[];
    mediaLinks: MediaLinkDocType[];
}

const COLLECTION_BY_TYPE: Record<
    ReferencingType,
    keyof AppDatabase['collections']
> = {
    planum: 'finding_planums',
    profile: 'finding_profiles',
    find: 'finds',
    sample: 'samples',
    photo: 'photos',
    drawingSheet: 'drawing_sheets',
    overallDescription: 'overall_descriptions'
};

const SUBJECT_BY_TYPE: Record<ReferencingType, Subject> = {
    planum: 'FindingPlanum',
    profile: 'FindingProfile',
    find: 'Find',
    sample: 'Sample',
    photo: 'Photo',
    drawingSheet: 'DrawingSheet',
    overallDescription: 'OverallDescription'
};

async function loadFamily(
    database: AppDatabase,
    familyIds: string[]
): Promise<FamilyRecords> {
    const selector = { selector: { projectId: { $in: familyIds } } };
    const load = async <T>(collection: RxCollection<T>): Promise<T[]> =>
        (await collection.find(selector as MangoQuery<T>).exec()).map(
            (doc) => doc.toMutableJSON() as T
        );
    const [
        findings,
        planums,
        profiles,
        finds,
        samples,
        photos,
        drawingSheets,
        overallDescriptions,
        media,
        mediaLinks
    ] = await Promise.all([
        load(database.findings),
        load(database.finding_planums),
        load(database.finding_profiles),
        load(database.finds),
        load(database.samples),
        load(database.photos),
        load(database.drawing_sheets),
        load(database.overall_descriptions),
        load(database.media),
        load(database.media_links)
    ]);
    return {
        findings,
        planums,
        profiles,
        finds,
        samples,
        photos,
        drawingSheets,
        overallDescriptions,
        media,
        mediaLinks
    };
}

function planFor(target: DeletionTarget, family: FamilyRecords): DeletionPlan {
    return planDeletion(target, {
        findings: family.findings,
        planums: family.planums,
        profiles: family.profiles,
        records: {
            find: family.finds,
            // A Probe names descriptions only, never a whole Befund.
            sample: family.samples.map((sample) => ({
                ...sample,
                findingIds: []
            })),
            photo: family.photos,
            drawingSheet: family.drawingSheets,
            overallDescription: family.overallDescriptions
        },
        media: family.media,
        mediaLinks: family.mediaLinks
    });
}

export interface DeletionImpactItem {
    key: string;
    label: string;
    /** The relations the reference is taken out of. */
    detail: string;
}

export interface DeletionImpact {
    /** Descriptions going down with a Befund (0 for a single description). */
    cascadedDescriptions: number;
    /** Pictures of everything that goes, by kind. */
    cascadedPhotos: number;
    cascadedSketches: number;
    /** Pictures that stay, because they are linked into a description that does. */
    movedMedia: number;
    /** Other descriptions that lose a reference, each named. */
    descriptions: DeletionImpactItem[];
    /**
     * Fotos, Zeichenblätter, Funde and Gesamtbeschreibungen losing their link,
     * only counted: a Befund can carry dozens of photos, and a list of file
     * names buries the descriptions that matter.
     */
    records: Record<RecordType, number>;
}

function descriptionItem(
    removal: ReferenceRemoval,
    family: FamilyRecords,
    intl: IntlShape
): DeletionImpactItem {
    const numberOf = (findingId: string) =>
        family.findings.find((finding) => finding.id === findingId)
            ?.findingNumber ?? '';
    const planum =
        removal.type === 'planum'
            ? family.planums.find((candidate) => candidate.id === removal.id)
            : undefined;
    const profile =
        removal.type === 'profile'
            ? family.profiles.find((candidate) => candidate.id === removal.id)
            : undefined;
    return {
        key: `${removal.type}:${removal.id}`,
        label: planum
            ? planumRefLabel(numberOf(planum.findingId), planum)
            : profile
              ? profileRefLabel(numberOf(profile.findingId), profile)
              : '—',
        detail: [...new Set(removal.fields.map(relationOfField))]
            .map((relation) =>
                intl.formatMessage({ id: `${removal.type}.field.${relation}` })
            )
            .join(', ')
    };
}

/**
 * Deleting a Befund or a Planum/Profil together with every reference to it.
 *
 * While `target` is set, the family is scanned once for what the deletion
 * touches, so the confirm dialog can list it (`impact`). `remove` scans again
 * before writing — the dialog may have stood open while a sync changed things.
 *
 * A Befund takes its descriptions with it; they used to stay behind, orphaned.
 * Pictures and sketches go with whatever they are attached to.
 * References on records this user may not edit are left alone: the push would
 * only be rejected.
 */
export function useFindingDeletion(
    projectId: string,
    target: DeletionTarget | null
): {
    impact: DeletionImpact | null;
    remove: () => Promise<void>;
} {
    const intl = useIntl();
    const database = useRxDatabase() as unknown as AppDatabase | undefined;
    const familyIds = useFamilyProjectIds(projectId);
    const ability = useAbility();
    // Keyed by the target it was computed for, so a stale scan never shows.
    const [scan, setScan] = useState<{
        key: string;
        impact: DeletionImpact;
    } | null>(null);

    const editable = useCallback(
        (removal: ReferenceRemoval) =>
            ability.can(
                'update',
                scopedSubject(SUBJECT_BY_TYPE[removal.type], {
                    projectId: removal.projectId
                })
            ),
        [ability]
    );

    const targetKind = target?.kind;
    const targetId = target?.id;
    const targetKey = target ? `${target.kind}:${target.id}` : '';
    useEffect(() => {
        if (!database || !targetKind || !targetId) {
            return;
        }
        let cancelled = false;
        void loadFamily(database, familyIds).then((family) => {
            if (cancelled) {
                return;
            }
            const plan = planFor(
                { kind: targetKind, id: targetId } as DeletionTarget,
                family
            );
            const editableRemovals = plan.removals.filter(editable);
            const descriptions = editableRemovals
                .filter(
                    (removal) =>
                        removal.type === 'planum' || removal.type === 'profile'
                )
                .map((removal) => descriptionItem(removal, family, intl))
                .sort((firstItem, secondItem) =>
                    compareNatural(firstItem.label, secondItem.label)
                );
            const records: Record<RecordType, number> = {
                photo: 0,
                drawingSheet: 0,
                find: 0,
                sample: 0,
                overallDescription: 0
            };
            for (const removal of editableRemovals) {
                if (removal.type !== 'planum' && removal.type !== 'profile') {
                    records[removal.type] += 1;
                }
            }
            setScan({
                key: `${targetKind}:${targetId}`,
                impact: {
                    cascadedDescriptions:
                        targetKind === 'finding'
                            ? plan.planumIds.length + plan.profileIds.length
                            : 0,
                    cascadedPhotos: plan.media.filter(
                        (item) => item.kind !== 'sketch'
                    ).length,
                    cascadedSketches: plan.media.filter(
                        (item) => item.kind === 'sketch'
                    ).length,
                    movedMedia: plan.mediaMoves.length,
                    descriptions,
                    records
                }
            });
        });
        return () => {
            cancelled = true;
        };
    }, [database, familyIds, targetKind, targetId, editable, intl]);

    const remove = useCallback(async () => {
        if (!database || !target) {
            return;
        }
        const plan = planFor(target, await loadFamily(database, familyIds));
        const now = new Date().toISOString();
        // References first: stopped half-way, a record without its reference
        // is better than a reference to nothing.
        for (const removal of plan.removals.filter(editable)) {
            const collection = database[
                COLLECTION_BY_TYPE[removal.type]
            ] as unknown as RxCollection;
            const doc = await collection.findOne(removal.id).exec();
            await doc?.incrementalPatch({ ...removal.patch, updatedAt: now });
        }
        await applyMediaRemoval(database, {
            deleteMediaIds: plan.media.map((item) => item.id),
            moves: plan.mediaMoves,
            deleteLinkIds: plan.mediaLinkIds,
            goingOwnerIds: [
                ...plan.findingIds,
                ...plan.planumIds,
                ...plan.profileIds
            ]
        });
        await removeOwnerTranslations(database, [
            ...plan.findingIds,
            ...plan.planumIds,
            ...plan.profileIds
        ]);
        for (const id of plan.planumIds) {
            await (await database.finding_planums.findOne(id).exec())?.remove();
        }
        for (const id of plan.profileIds) {
            await (
                await database.finding_profiles.findOne(id).exec()
            )?.remove();
        }
        for (const id of plan.findingIds) {
            await (await database.findings.findOne(id).exec())?.remove();
        }
    }, [database, familyIds, target, editable]);

    return {
        impact: scan && scan.key === targetKey ? scan.impact : null,
        remove
    };
}
